// Danh mục quyền và vai trò mẫu — nguồn sự thật dùng chung cho identity-service, gateway và web.
// Khoá quyền: <module>.<hanhDong>. scoped = nhận phạm vi own/team/all; level admin = cần quyền quản trị cấp tenant.
import type { Grant } from './context.js';

export interface PermissionDef {
  key: string;
  label: string;
  scoped?: boolean;
  level?: 'admin';
  sensitive?: boolean;
}
export interface PermissionGroup {
  module: string;
  label: string;
  section: string;
  permissions: PermissionDef[];
}

export const PERMISSION_CATALOG: PermissionGroup[] = [
  {
    "module": "customers",
    "label": "Khách hàng",
    "section": "source",
    "permissions": [
      {
        "key": "customers.view",
        "label": "Xem khách hàng",
        "scoped": true
      },
      {
        "key": "customers.create",
        "label": "Tạo khách, nhận khách hết bảo vệ"
      },
      {
        "key": "customers.edit",
        "label": "Sửa thông tin, ghi nhận chăm sóc",
        "scoped": true
      },
      {
        "key": "customers.assign",
        "label": "Phân, chuyển, thu hồi khách (gồm kho chung)",
        "scoped": true
      },
      {
        "key": "customers.phone",
        "label": "Xem số điện thoại đầy đủ",
        "scoped": true
      },
      {
        "key": "customers.import",
        "label": "Nhập khách từ Excel"
      },
      {
        "key": "customers.export",
        "label": "Xuất toàn bộ danh sách khách",
        "sensitive": true
      }
    ]
  },
  {
    "module": "inventory",
    "label": "Nguồn hàng",
    "section": "source",
    "permissions": [
      {
        "key": "inventory.view",
        "label": "Xem dự án, bảng giá"
      },
      {
        "key": "inventory.edit",
        "label": "Tạo dự án, sửa chính sách, bảng hàng, giá",
        "level": "admin"
      },
      {
        "key": "inventory.owner",
        "label": "Xem thông tin chủ nhà hàng ký gửi",
        "sensitive": true
      }
    ]
  },
  {
    "module": "booking",
    "label": "Chốt căn",
    "section": "sales",
    "permissions": [
      {
        "key": "booking.view",
        "label": "Xem bảng hàng trực tuyến"
      },
      {
        "key": "booking.lock",
        "label": "Giữ căn, xếp hàng, gửi phiếu cọc"
      },
      {
        "key": "booking.override",
        "label": "Gia hạn / mở khoá căn người khác đang giữ"
      }
    ]
  },
  {
    "module": "schedule",
    "label": "Lịch hẹn & việc",
    "section": "sales",
    "permissions": [
      {
        "key": "tasks.view",
        "label": "Xem lịch hẹn, công việc",
        "scoped": true
      },
      {
        "key": "tasks.assign",
        "label": "Giao lịch cho người khác",
        "scoped": true
      }
    ]
  },
  {
    "module": "deals",
    "label": "Giao dịch & hợp đồng",
    "section": "deals",
    "permissions": [
      {
        "key": "deals.view",
        "label": "Xem giao dịch",
        "scoped": true
      },
      {
        "key": "deals.approve",
        "label": "Duyệt / từ chối phiếu cọc"
      },
      {
        "key": "deals.manage",
        "label": "Ký HĐ, ghi nhận thanh toán, đổi căn, phí CĐT"
      },
      {
        "key": "deals.cancel",
        "label": "Huỷ giao dịch",
        "sensitive": true
      }
    ]
  },
  {
    "module": "attendance",
    "label": "Chấm công",
    "section": "performance",
    "permissions": [
      {
        "key": "attendance.self",
        "label": "Chấm công, gửi đơn nghỉ của bản thân"
      },
      {
        "key": "attendance.view",
        "label": "Xem bảng công",
        "scoped": true
      },
      {
        "key": "attendance.adjust",
        "label": "Điều chỉnh ngày công",
        "scoped": true,
        "sensitive": true
      },
      {
        "key": "leaves.approve",
        "label": "Duyệt đơn nghỉ",
        "scoped": true
      }
    ]
  },
  {
    "module": "sales",
    "label": "Doanh số & KPI",
    "section": "performance",
    "permissions": [
      {
        "key": "sales.view",
        "label": "Xem doanh số, xếp hạng",
        "scoped": true
      },
      {
        "key": "sales.targets",
        "label": "Đặt chỉ tiêu tháng",
        "scoped": true
      }
    ]
  },
  {
    "module": "commission",
    "label": "Hoa hồng & thu nhập",
    "section": "finance",
    "permissions": [
      {
        "key": "commission.self",
        "label": "Xem hoa hồng và phiếu thu nhập của mình"
      },
      {
        "key": "commission.view",
        "label": "Xem sổ hoa hồng, bảng thu nhập toàn sàn",
        "sensitive": true
      },
      {
        "key": "payout.manage",
        "label": "Chốt kỳ chi trả, đánh dấu đã chi",
        "level": "admin"
      },
      {
        "key": "payout.approve",
        "label": "Duyệt kỳ chi trả",
        "level": "admin"
      }
    ]
  },
  {
    "module": "hr",
    "label": "Nhân sự",
    "section": "system",
    "permissions": [
      {
        "key": "hr.view",
        "label": "Xem hồ sơ nhân sự, nhóm",
        "scoped": true
      },
      {
        "key": "hr.manage",
        "label": "Thêm, sửa hồ sơ, nhóm, cho nghỉ việc & bàn giao",
        "level": "admin"
      },
      {
        "key": "salary.manage",
        "label": "Xem, sửa lương cơ bản",
        "level": "admin",
        "sensitive": true
      }
    ]
  },
  {
    "module": "accounts",
    "label": "Tài khoản & quyền",
    "section": "system",
    "permissions": [
      {
        "key": "accounts.view",
        "label": "Xem tài khoản, vai trò"
      },
      {
        "key": "accounts.manage",
        "label": "Duyệt tài khoản, gán vai trò, khoá / mở khoá",
        "level": "admin",
        "sensitive": true
      },
      {
        "key": "roles.manage",
        "label": "Tạo, sửa vai trò và quyền",
        "level": "admin",
        "sensitive": true
      }
    ]
  },
  {
    "module": "settings",
    "label": "Cài đặt",
    "section": "system",
    "permissions": [
      {
        "key": "settings.manage",
        "label": "Sửa cài đặt công ty, quy tắc, chính sách",
        "level": "admin",
        "sensitive": true
      },
      {
        "key": "modules.manage",
        "label": "Bật / tắt module",
        "level": "admin"
      }
    ]
  },
  {
    "module": "audit",
    "label": "Nhật ký",
    "section": "system",
    "permissions": [
      {
        "key": "audit.view",
        "label": "Xem nhật ký hoạt động"
      }
    ]
  }
];

export const PERMISSIONS: PermissionDef[] = PERMISSION_CATALOG.flatMap((g) => g.permissions);
export const permissionDef = (key: string) => PERMISSIONS.find((p) => p.key === key) ?? null;

export interface RoleTemplate {
  name: string;
  description: string;
  /** "*" = toàn quyền, kể cả quyền thêm sau */
  perms: "*" | Record<string, Grant>;
}

export const SYSTEM_ROLES: Record<string, RoleTemplate> = {
  "admin": {
    "name": "Quản trị hệ thống",
    "description": "Toàn quyền, gồm tài khoản, vai trò, cài đặt và chi trả.",
    "perms": "*"
  },
  "manager": {
    "name": "Quản lý sàn",
    "description": "Điều hành kinh doanh toàn sàn, phân khách, duyệt nghỉ, đặt chỉ tiêu.",
    "perms": {
      "customers.view": "all",
      "customers.create": true,
      "customers.edit": "all",
      "customers.assign": "all",
      "customers.phone": "all",
      "customers.import": true,
      "inventory.view": true,
      "booking.view": true,
      "booking.lock": true,
      "booking.override": true,
      "tasks.view": "all",
      "tasks.assign": "all",
      "deals.view": "all",
      "deals.cancel": true,
      "attendance.self": true,
      "attendance.view": "all",
      "attendance.adjust": "team",
      "leaves.approve": "all",
      "sales.view": "all",
      "sales.targets": "all",
      "commission.self": true,
      "commission.view": true,
      "hr.view": "all",
      "accounts.view": true,
      "audit.view": true
    }
  },
  "leader": {
    "name": "Trưởng nhóm",
    "description": "Quản lý khách, lịch, doanh số và công của nhóm mình.",
    "perms": {
      "customers.view": "team",
      "customers.create": true,
      "customers.edit": "team",
      "customers.assign": "team",
      "customers.phone": "team",
      "inventory.view": true,
      "booking.view": true,
      "booking.lock": true,
      "tasks.view": "team",
      "tasks.assign": "team",
      "deals.view": "team",
      "attendance.self": true,
      "attendance.view": "team",
      "leaves.approve": "team",
      "sales.view": "team",
      "sales.targets": "team",
      "commission.self": true,
      "hr.view": "team"
    }
  },
  "sale": {
    "name": "Chuyên viên kinh doanh",
    "description": "Chăm sóc khách của mình, giữ căn, gửi phiếu cọc, chấm công.",
    "perms": {
      "customers.view": "own",
      "customers.create": true,
      "customers.edit": "own",
      "customers.phone": "own",
      "inventory.view": true,
      "booking.view": true,
      "booking.lock": true,
      "tasks.view": "own",
      "deals.view": "own",
      "attendance.self": true,
      "sales.view": "own",
      "commission.self": true
    }
  },
  "ctv": {
    "name": "Cộng tác viên",
    "description": "Như chuyên viên kinh doanh nhưng không chấm công; thu nhập theo hoa hồng.",
    "perms": {
      "customers.view": "own",
      "customers.create": true,
      "customers.edit": "own",
      "customers.phone": "own",
      "inventory.view": true,
      "booking.view": true,
      "booking.lock": true,
      "tasks.view": "own",
      "deals.view": "own",
      "sales.view": "own",
      "commission.self": true
    }
  },
  "salesadmin": {
    "name": "Admin dự án",
    "description": "Quản lý nguồn hàng, bảng giá; duyệt cọc, hợp đồng, thanh toán.",
    "perms": {
      "customers.view": "all",
      "customers.phone": "all",
      "inventory.view": true,
      "inventory.edit": true,
      "inventory.owner": true,
      "booking.view": true,
      "booking.override": true,
      "tasks.view": "own",
      "deals.view": "all",
      "deals.approve": true,
      "deals.manage": true,
      "deals.cancel": true,
      "attendance.self": true,
      "sales.view": "all",
      "commission.self": true
    }
  },
  "accountant": {
    "name": "Kế toán",
    "description": "Đối soát giao dịch, hoa hồng, lương và kỳ chi trả.",
    "perms": {
      "inventory.view": true,
      "booking.view": true,
      "deals.view": "all",
      "attendance.self": true,
      "attendance.view": "all",
      "sales.view": "all",
      "commission.self": true,
      "commission.view": true,
      "payout.manage": true,
      "salary.manage": true,
      "hr.view": "all"
    }
  },
  "hr": {
    "name": "Hành chính nhân sự",
    "description": "Hồ sơ nhân sự, tài khoản, chấm công và đơn nghỉ.",
    "perms": {
      "inventory.view": true,
      "booking.view": true,
      "attendance.self": true,
      "attendance.view": "all",
      "attendance.adjust": "all",
      "leaves.approve": "all",
      "commission.self": true,
      "hr.view": "all",
      "hr.manage": true,
      "salary.manage": true,
      "accounts.view": true,
      "accounts.manage": true
    }
  }
};
